import * as React from 'react';

// UTILS
import { emailRegex } from '../../utils';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import Button from '../../components/Button';
import SearchOtherRecipients from './SearchOtherRecipients';

// ICONS
import { ReactComponent as EmailIcon } from '../../assets/svg/email.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';

// TYPES
import { IncidentFormTypes } from './IncidentSubmit';
import { IncidentArchiveEditUser } from '../../api/models';

export interface StepTwoTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	leadershipTeamData: IncidentArchiveEditUser[];
	otherRecipientsData: IncidentArchiveEditUser[];
	setOtherRecipientsData: React.Dispatch<React.SetStateAction<IncidentArchiveEditUser[]>>;
}

import { IncidentArchiveRecipientsUser } from '../../api/models/incident-archive-recipients-user';

const StepTwo: React.FC<StepTwoTypes> = ({
	incidentFormData,
	setIncidentFormData,

	setActiveStep,

	leadershipTeamData,

	otherRecipientsData,
	setOtherRecipientsData
}) => {
	// Error state
	const [showError, setShowError] = React.useState(false);

	// LOGIC FOR LEADERSHIP
	const [selectedLeadershipMemberId, setSelectedLeadershipMemberId] = React.useState<string[]>([]);

	React.useEffect(() => {
		setSelectedLeadershipMemberId(incidentFormData.leaderRecipientIds || []);

		setOtherRecipientsData(incidentFormData.otherRecipients || []);

		setSelectedEmails(incidentFormData.otherRecipientEmails || []);
	}, []);

	const selectLeadershipMemberHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;

		if (checked) {
			if (!selectedLeadershipMemberId.includes(value)) {
				setSelectedLeadershipMemberId((prev) => [...prev, value]);
			}
		} else {
			setSelectedLeadershipMemberId((prev) => prev.filter((item) => item !== value));
		}

		if (showError) {
			setShowError(false);
		}
	};

	const removeSelectLeadershipMemberHandler = (id: string | undefined) => {
		if (id !== undefined) {
			setSelectedLeadershipMemberId((prev) => prev.filter((item) => item !== id));
		}
	};

	// LOGIC FOR OTHER RECIPENTS
	const [showExistingMemberError, setShowExistingMemberError] = React.useState<boolean>(false);

	const addOtherRecipientsHandler = (item: IncidentArchiveRecipientsUser) => {
		if (item !== undefined && item.id !== undefined && !incidentFormData.otherRecipientIds?.includes(item.id)) {
			setShowExistingMemberError(false);

			setOtherRecipientsData([...otherRecipientsData, item]);

			setIncidentFormData({
				...incidentFormData,

				otherRecipientIds: [...(incidentFormData.otherRecipientIds || []), item.id || '']
			});
		} else if (item !== undefined && item.id !== undefined && incidentFormData.otherRecipientIds?.includes(item.id)) {
			setShowExistingMemberError(true);
		}
	};

	const removeOtherRecipientsHandler = (id: string | undefined) => {
		if (id !== undefined) {
			const newList = otherRecipientsData.filter((item) => item.id !== id);

			setOtherRecipientsData(newList);

			const incidentIds =
				incidentFormData.otherRecipientIds && incidentFormData.otherRecipientIds.filter((item) => item !== id);

			setIncidentFormData({
				...incidentFormData,

				otherRecipientIds: [...(incidentIds || [])]
			});
		}
	};

	React.useEffect(() => {
		setOtherRecipientsData(otherRecipientsData);
	}, [otherRecipientsData]);

	// LOGIC FOR EMAIL INPUT
	const [selectedEmails, setSelectedEmails] = React.useState<string[]>(incidentFormData.otherRecipientEmails || []);

	const [emailInput, setEmailInput] = React.useState<string>('');

	const [emailInputError, setEmailInputError] = React.useState<boolean>(false);

	const [isAddEmailFieldActive, setIsAddEmailFieldActive] = React.useState(false);

	React.useEffect(() => {
		setSelectedEmails(selectedEmails);
	}, [selectedEmails]);

	const emailInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmailInput = e.target.value;

		setEmailInput(newEmailInput);

		if (emailInputError && newEmailInput.match(emailRegex)) {
			setEmailInputError(false);
		}
	};

	const addEmailHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (emailInput.match(emailRegex)) {
			setEmailInputError(false);

			setSelectedEmails((prevState) => [...prevState, emailInput]);

			setEmailInput('');

			setIncidentFormData({
				...incidentFormData,

				otherRecipientEmails: [...(incidentFormData.otherRecipientEmails || []), emailInput]
			});
		} else {
			setEmailInputError(true);
		}
	};

	const removeEmailHandler = (index: number) => {
		const newSelectedEmail = selectedEmails.filter((item, itemIndex) => itemIndex !== index);
		setSelectedEmails(newSelectedEmail);

		setIncidentFormData({
			...incidentFormData,
			otherRecipientEmails: [...(newSelectedEmail || [])]
		});
	};

	// SUBMIT LOGIC
	const onSubmitStepTwo = (values: any) => {
		if (selectedLeadershipMemberId.length < 1) {
			setShowError(true);
		} else {
			setShowError(false);

			setIncidentFormData({
				...incidentFormData,

				leaderRecipientIds: [...selectedLeadershipMemberId]
			});

			setActiveStep('step3');

			window.scrollTo(0, 0);
		}
	};

	return (
		<div className="general-form">
			<div className="general-form-layout-wrapper">
				<div className="form-main w-100">
					<label htmlFor="contactsNotifiedInput" className="general-header">
						Which leaders of your organisation must be notified of this incident?
					</label>

					<div className="general-divider mbtom-30"></div>

					<div className="form-group">
						<div className="form-field">
							{leadershipTeamData &&
								leadershipTeamData.length > 0 &&
								leadershipTeamData.map((item, index) => {
									return (
										<div key={index} className="checkbox-row">
											<input
												type="checkbox"
												name="leaderRecipientIdsInput"
												value={item.id}
												onChange={selectLeadershipMemberHandler}
												checked={selectedLeadershipMemberId.includes(item.id || '')}
											/>
											<label>{item.name}</label>
										</div>
									);
								})}
						</div>
					</div>
				</div>
			</div>

			<div className="general-divider mbtom-30"></div>
			<h2 className="general-header">Who else should be notified about this incident?</h2>

			<p className="sub-grey-text">
				Search by name for contacts you wish to notify. If a contact is not listed, add them via email below.
			</p>

			<section className="people-search-wrapper">
				<h2 className="search-heading">Search</h2>

				<SearchOtherRecipients
					placeholder="Begin typing a staff name..."
					onChange={(item: IncidentArchiveRecipientsUser) => addOtherRecipientsHandler(item)}
					setShowExistingMemberError={setShowExistingMemberError}
				/>

				{showExistingMemberError && <div className="error-message">This member has already been added</div>}
			</section>

			<div className="or-divider w-50">
				<div className="text-wrapper">or</div>
			</div>

			<section className="add-email-wrapper">
				<h2 className="add-email-heading">Enter email address if contact isn't listed above.</h2>

				<div className="add-email-content" onClick={() => setIsAddEmailFieldActive(!isAddEmailFieldActive)}>
					<div className={`add-email-container ${isAddEmailFieldActive ? 'active' : ''}`}>
						<EmailIcon className="email-icon" />

						<input
							className="email-input"
							type="text"
							placeholder="Enter an email..."
							onChange={emailInputHandler}
							value={emailInput}
						/>
					</div>

					<Button className="btn-secondary" onClick={addEmailHandler}>
						Add
					</Button>
				</div>

				{emailInputError && <div className="error-message">Invalid email format</div>}
			</section>

			<h2 className="general-header">Recipient of the incident report</h2>

			<section className="info-section incident-form">
				{leadershipTeamData && leadershipTeamData.length > 0 && (
					<ul>
						{leadershipTeamData.map((item) => {
							if (item.id && selectedLeadershipMemberId.includes(item.id)) {
								return (
									<li key={item.id} className="row-container">
										<div className="row detail-row">
											<div className="col-2 col-md-1 pdl-0">
												<CircleImage
													extraClass="selected-incident-icon"
													firstName={item.name}
													lastName={item.name}
													src={item.profileImage}
												/>
											</div>

											<div className="col-8 col-md-9 pdl-0">
												<div className="row detail-info center">
													<div className="col-lg-8 detail-name">{item.name}</div>

													<div className="col-lg-4 detail-status">
														<span className="label grey">{item.role}</span>
													</div>
												</div>
											</div>

											<div className="col-2 link-group pdr-0">
												<Button className="btn-text pdr-0" onClick={() => removeSelectLeadershipMemberHandler(item.id)}>
													Remove
												</Button>
											</div>
										</div>
									</li>
								);
							} else return null;
						})}
					</ul>
				)}

				<ul>
					{otherRecipientsData &&
						otherRecipientsData.length > 0 &&
						otherRecipientsData.map((item, index) => {
							return (
								<li key={index} className="row-container">
									<div className="row detail-row">
										<div className="col-2 col-md-1 pdl-0">
											<CircleImage
												extraClass="selected-incident-icon"
												firstName={item.name}
												lastName={item.name}
												src={item.profileImage}
											/>
										</div>

										<div className="col-8 col-md-9 pdl-0">
											<div className="row detail-info center">
												<div className="col-lg-8 detail-name">{item.name}</div>

												<div className="col-lg-4 detail-status">
													<span className="label grey">{item.role}</span>
												</div>
											</div>
										</div>

										<div className="col-2 link-group pdr-0">
											<Button className="btn-text pdr-0" onClick={() => removeOtherRecipientsHandler(item.id)}>
												Remove
											</Button>
										</div>
									</div>
								</li>
							);
						})}
				</ul>

				<ul>
					{selectedEmails &&
						selectedEmails.length > 0 &&
						selectedEmails.map((item, index) => {
							return (
								<li key={index} className="row-container">
									<div className="row detail-row">
										<div className="col-2 col-md-1 pdl-0">
											<EnvelopeIcon className="selected-incident-icon" />
										</div>

										<div className="col-8 col-md-9 pdl-0">
											<div className="row detail-info center">
												<div className="col-12 detail-name ellipsis">{item}</div>
											</div>
										</div>

										<div className="col-2 link-group pdr-0">
											<Button className="btn-text pdr-0 mtop-0" onClick={() => removeEmailHandler(index)}>
												Remove
											</Button>
										</div>
									</div>
								</li>
							);
						})}
				</ul>
			</section>

			<div className="general-divider mbtom-30 mtop-50"></div>

			{showError && <div className="error-message mtop-20">You need to add at least 1 leadership Team member</div>}

			<div className="form-button-group">
				<Button type="submit" className="btn-primary submit-btn complaints-submit-page" onClick={onSubmitStepTwo}>
					Continue
				</Button>
			</div>
		</div>
	);
};

export default StepTwo;
